"use client";

/**
 * ReelsFeed - Instagram/TikTok-style vertical reels feed
 *
 * Optimized for performance:
 * - Single video at a time (no overlapping audio/video)
 * - Cached API calls (see muxApi.ts)
 * - Minimal re-renders
 * - Native-feeling swipe gestures
 */

import { getReelsMuxPlaybackIds } from "@/lib/muxApi";
import MuxPlayer from "@mux/mux-player-react";
import {
  ChevronDown,
  ChevronUp,
  Heart,
  Play,
  RefreshCw,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Hook to detect mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// Hook to detect iOS
const useIsIOS = () => {
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice =
      /iphone|ipad|ipod/.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
  }, []);

  return isIOS;
};

export type ReelsWatchProgress = {
  index: number;
  playbackId: string;
  /** 0–100 of current video */
  progressPct: number;
  /** currentTime seconds of current video */
  watchedSeconds: number;
  /** seconds advanced since last progress callback (approx) */
  deltaSeconds: number;
  totalVideos: number;
  completed?: boolean;
};

interface ReelsFeedProps {
  onVideoPlay?: (index: number, playbackId: string) => void;
  onVideoChange?: (index: number, playbackId: string) => void;
  /** Watch progress milestones / heartbeats for secret-share analytics */
  onWatchProgress?: (p: ReelsWatchProgress) => void;
  fallbackPlaybackIds?: string[];
  /**
   * Server-authoritative playlist (private /reels grant).
   * When **defined** (including `[]`), never fetch public Mux — empty means empty.
   * When **undefined**, public /reels-style clients may load via getReelsMuxPlaybackIds.
   */
  initialPlaybackIds?: string[];
  /** 0 = unlimited; caps how far the user can swipe */
  maxViews?: number;
  onReachEnd?: () => void;
  /** Empty-state copy when playlist is empty */
  emptyMessage?: string;
  /** Mux Data env key (optional) */
  muxDataEnvKey?: string;
  /** Extra Mux Data metadata (viewer_user_id, custom fields, etc.) */
  muxDataMetadata?: Record<string, string | number | undefined>;
}

const ReelsFeed: React.FC<ReelsFeedProps> = ({
  onVideoPlay,
  onVideoChange,
  onWatchProgress,
  fallbackPlaybackIds = [],
  initialPlaybackIds,
  maxViews = 0,
  onReachEnd,
  emptyMessage = "No hay videos disponibles",
  muxDataEnvKey,
  muxDataMetadata,
}) => {
  // Core state
  const [playbackIds, setPlaybackIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isBuffering, setIsBuffering] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Touch/swipe state
  const [touchStart, setTouchStart] = useState<{
    y: number;
    time: number;
  } | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const hasReachedEndRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const progressMilestonesRef = useRef<Set<number>>(new Set());
  const lastWatchEmitRef = useRef(0);
  const lastCurrentTimeRef = useRef(0);
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();

  // Update document title
  useEffect(() => {
    if (isLoading) {
      document.title = "...";
    } else if (showEndScreen) {
      document.title = "fin";
    } else if (playbackIds.length > 0) {
      document.title = String(currentIndex + 1);
    }
  }, [currentIndex, isLoading, showEndScreen, playbackIds.length]);

  // Load playlist: server grant is authoritative (even when empty). Public path only if unset.
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const applyCap = (ids: string[]) => {
      const unique = [...new Set(ids)];
      if (maxViews > 0 && unique.length > maxViews) {
        return unique.slice(0, maxViews);
      }
      return unique;
    };

    const fetchReels = async () => {
      setIsLoading(true);
      try {
        // Defined (incl. []) = private grant / server playlist — do not hit public Mux.
        if (initialPlaybackIds !== undefined) {
          setPlaybackIds(applyCap(initialPlaybackIds));
          return;
        }
        const ids = await getReelsMuxPlaybackIds();
        if (ids.length > 0) {
          setPlaybackIds(applyCap(ids));
        } else if (fallbackPlaybackIds.length > 0) {
          setPlaybackIds(applyCap(fallbackPlaybackIds));
        }
      } catch (error) {
        console.error("Failed to fetch reels:", error);
        if (initialPlaybackIds === undefined && fallbackPlaybackIds.length > 0) {
          setPlaybackIds(applyCap(fallbackPlaybackIds));
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchReels();
  }, [fallbackPlaybackIds, initialPlaybackIds, maxViews]);

  // Reset video state when changing videos
  useEffect(() => {
    setIsBuffering(true);
    setHasError(false);
    setProgress(0);
    setIsPlaying(true);
    progressMilestonesRef.current = new Set();
    lastWatchEmitRef.current = 0;
    lastCurrentTimeRef.current = 0;
    if (playbackIds[currentIndex] && currentIndex >= 0) {
      onVideoChange?.(currentIndex, playbackIds[currentIndex]);
    }
  }, [currentIndex, onVideoChange, playbackIds]);

  // Check if reached end
  useEffect(() => {
    if (showEndScreen && !hasReachedEndRef.current) {
      hasReachedEndRef.current = true;
      onReachEnd?.();
    }
  }, [showEndScreen, onReachEnd]);

  // Navigate to a specific video
  const goToVideo = useCallback(
    (index: number) => {
      if (isTransitioning) return;

      // Going to end screen
      if (index >= playbackIds.length) {
        setIsTransitioning(true);
        if (playerRef.current?.pause) playerRef.current.pause();

        setTimeout(() => {
          setShowEndScreen(true);
          setIsTransitioning(false);
        }, 200);
        return;
      }

      if (index < 0) return;

      // Coming back from end screen
      if (showEndScreen) {
        setShowEndScreen(false);
      }

      setIsTransitioning(true);

      // Pause current video
      if (playerRef.current?.pause) playerRef.current.pause();

      // Update index — onVideoChange fires once from the currentIndex effect
      setCurrentIndex(index);

      setTimeout(() => {
        setIsTransitioning(false);
      }, 200);
    },
    [playbackIds, isTransitioning, showEndScreen]
  );

  const goToNext = useCallback(() => {
    if (showEndScreen) return;
    goToVideo(currentIndex + 1);
  }, [currentIndex, goToVideo, showEndScreen]);

  const goToPrevious = useCallback(() => {
    if (showEndScreen) {
      setShowEndScreen(false);
      goToVideo(playbackIds.length - 1);
      return;
    }
    if (currentIndex > 0) {
      goToVideo(currentIndex - 1);
    }
  }, [currentIndex, goToVideo, showEndScreen, playbackIds.length]);

  const restartFromBeginning = useCallback(() => {
    setShowEndScreen(false);
    hasReachedEndRef.current = false;
    goToVideo(0);
  }, [goToVideo]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart({ y: touch.clientY, time: Date.now() });
    setSwipeOffset(0);
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart) return;
      const touch = e.touches[0];
      const deltaY = touchStart.y - touch.clientY;
      const resistance = 0.4;
      const maxOffset = 150;
      const offset =
        Math.sign(deltaY) * Math.min(Math.abs(deltaY) * resistance, maxOffset);
      setSwipeOffset(offset);
    },
    [touchStart]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart) return;

      const touch = e.changedTouches[0];
      const deltaY = touchStart.y - touch.clientY;
      const deltaTime = Date.now() - touchStart.time;
      const velocity = Math.abs(deltaY) / deltaTime;

      setSwipeOffset(0);

      const swipeThreshold = isIOS ? 40 : 50;
      const velocityThreshold = isIOS ? 0.25 : 0.3;
      const isSwipe =
        Math.abs(deltaY) > swipeThreshold || velocity > velocityThreshold;

      if (isSwipe) {
        if (deltaY > 0) {
          goToNext();
        } else {
          goToPrevious();
        }
      }

      setTouchStart(null);
    },
    [touchStart, goToNext, goToPrevious, isIOS]
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        goToNext();
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === "m") {
        setIsMuted((prev) => !prev);
      } else if (e.key === " ") {
        e.preventDefault();
        if (showEndScreen) {
          restartFromBeginning();
          return;
        }
        if (playerRef.current) {
          if (playerRef.current.paused) {
            playerRef.current.play();
            setIsPlaying(true);
          } else {
            playerRef.current.pause();
            setIsPlaying(false);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToNext, goToPrevious, showEndScreen, restartFromBeginning]);

  // Wheel navigation (desktop)
  useEffect(() => {
    if (isMobile) return;

    let lastWheelTime = 0;
    const wheelThrottle = 500;

    const handleWheel = (e: WheelEvent) => {
      const now = Date.now();
      if (now - lastWheelTime < wheelThrottle) return;

      if (Math.abs(e.deltaY) > 50) {
        lastWheelTime = now;
        if (e.deltaY > 0) {
          goToNext();
        } else {
          goToPrevious();
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: true });
      return () => container.removeEventListener("wheel", handleWheel);
    }
  }, [isMobile, goToNext, goToPrevious]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (playerRef.current) {
      if (playerRef.current.paused) {
        playerRef.current.play();
        setIsPlaying(true);
      } else {
        playerRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, []);

  const handleVideoTap = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if ("touches" in e) return;
      togglePlayPause();
    },
    [togglePlayPause]
  );

  // Video event handlers - simple and direct
  const handleCanPlay = useCallback(() => {
    setIsBuffering(false);
    // Auto-play when ready
    if (playerRef.current && playerRef.current.paused) {
      playerRef.current.play().catch(() => {});
    }
  }, []);

  // When video ends, go to next
  const handleEnded = useCallback(() => {
    goToNext();
  }, [goToNext]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setIsBuffering(false);
    onVideoPlay?.(currentIndex, playbackIds[currentIndex]);
  }, [currentIndex, onVideoPlay, playbackIds]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleWaiting = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handlePlaying = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleError = useCallback(() => {
    setIsBuffering(false);
    setHasError(true);
  }, []);

  const handleTimeUpdate = useCallback(
    (e: Event) => {
      const video = e.target as HTMLVideoElement | null;
      if (!video || !video.duration || !Number.isFinite(video.duration)) return;
      const prog = (video.currentTime / video.duration) * 100;
      setProgress(prog);

      if (!onWatchProgress) return;
      const pb = playbackIds[currentIndex];
      if (!pb) return;

      const now = Date.now();
      const delta = Math.max(
        0,
        Math.min(5, video.currentTime - lastCurrentTimeRef.current),
      );
      lastCurrentTimeRef.current = video.currentTime;

      const floor = Math.floor(prog);
      const milestones = [10, 25, 50, 75, 90, 100];
      for (const m of milestones) {
        if (floor >= m && !progressMilestonesRef.current.has(m)) {
          progressMilestonesRef.current.add(m);
          onWatchProgress({
            index: currentIndex,
            playbackId: pb,
            progressPct: m,
            watchedSeconds: Math.floor(video.currentTime),
            deltaSeconds: delta,
            totalVideos: playbackIds.length,
            completed: m === 100,
          });
        }
      }

      // Heartbeat every ~8s for watch-time aggregation
      if (now - lastWatchEmitRef.current >= 8000 && delta > 0) {
        lastWatchEmitRef.current = now;
        onWatchProgress({
          index: currentIndex,
          playbackId: pb,
          progressPct: Math.min(100, Math.floor(prog)),
          watchedSeconds: Math.floor(video.currentTime),
          deltaSeconds: Math.min(10, Math.max(1, Math.round(delta * 2) || 8)),
          totalVideos: playbackIds.length,
        });
      }
    },
    [currentIndex, onWatchProgress, playbackIds],
  );

  const retryVideo = useCallback(() => {
    setHasError(false);
    setIsBuffering(true);
    // Force remount by briefly clearing and restoring
    const idx = currentIndex;
    setCurrentIndex(-1);
    setTimeout(() => setCurrentIndex(idx), 50);
  }, [currentIndex]);

  // Styles
  const videoStyles = useMemo(
    () => `
    .reels-feed-container {
      width: 100%;
      height: 100dvh;
      height: 100vh;
      overflow: hidden;
      position: fixed;
      inset: 0;
      background: #000;
      touch-action: pan-y;
      -webkit-overflow-scrolling: touch;
    }

    .reels-video-wrapper {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #000;
    }

    .reels-mux-player {
      width: 100% !important;
      height: 100% !important;
      max-width: 100vw !important;
      max-height: 100dvh !important;
      max-height: 100vh !important;
      --controls: none !important;
      --media-object-fit: cover !important;
    }

    .reels-mux-player video {
      object-fit: cover !important;
      width: 100% !important;
      height: 100% !important;
    }

    @media (min-width: 768px) {
      .reels-mux-player {
        max-width: min(56.25vh, 100vw) !important;
        --media-object-fit: contain !important;
      }
      .reels-mux-player video {
        object-fit: contain !important;
      }
    }

    @supports (-webkit-touch-callout: none) {
      .reels-feed-container {
        height: -webkit-fill-available;
      }
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .animate-fade-in-up {
      animation: fadeInUp 0.5s ease-out forwards;
    }

    .progress-bar {
      transition: width 0.1s linear;
    }

    @keyframes pulse-ring {
      0% { transform: scale(0.8); opacity: 1; }
      100% { transform: scale(1.3); opacity: 0; }
    }

    .buffering-ring {
      animation: pulse-ring 1s ease-out infinite;
    }
  `,
    []
  );

  // Current playback ID
  const currentPlaybackId = playbackIds[currentIndex];

  // Loading screen
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-12 h-12 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-2 border-white/20" />
            <div className="absolute inset-0 rounded-full border-2 border-white border-t-transparent animate-spin" />
          </div>
          <p className="text-white/60 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  // Empty playlist is valid (admin has not uploaded yet)
  if (playbackIds.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center px-8 max-w-sm">
          <p className="text-white/70 text-sm mb-2">{emptyMessage}</p>
          <p className="text-white/35 text-xs mb-6">
            El feed puede estar vacío hasta que se suban videos desde el admin.
          </p>
          <a
            href="/"
            className="text-white/40 hover:text-white/60 text-xs transition-colors"
          >
            ← Volver
          </a>
        </div>
      </div>
    );
  }

  // End screen
  if (showEndScreen) {
    return (
      <>
        <style>{videoStyles}</style>
        <div
          ref={containerRef}
          className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="relative z-10 text-center px-8 max-w-md">
            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "0ms" }}
            >
              <h1 className="text-xl md:text-2xl font-light text-white mb-6 leading-relaxed">
                Hay días y noches que cuestan más,
                <br />
                <span className="font-medium">
                  pero ahí se nota la diferencia de quienes se rinden y quienes
                  siguen adelante
                </span>
              </h1>
            </div>

            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "300ms" }}
            >
              <Heart className="w-5 h-5 text-white/30 mx-auto" />
            </div>
          </div>

          <div
            className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-fade-in-up"
            style={{ animationDelay: "600ms" }}
          >
            <button
              onClick={restartFromBeginning}
              className="flex items-center gap-2 text-white/30 hover:text-white/50 text-xs transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              <span>ver de nuevo</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{videoStyles}</style>

      <div
        ref={containerRef}
        className="reels-feed-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform:
            swipeOffset !== 0 ? `translateY(${-swipeOffset}px)` : undefined,
          transition: swipeOffset === 0 ? "transform 0.2s ease-out" : "none",
        }}
      >
        {/* Single video player */}
        {currentPlaybackId && currentIndex >= 0 && (
          <div className="reels-video-wrapper" onClick={handleVideoTap}>
            {/* Buffering indicator */}
            {isBuffering && !hasError && (
              <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border-2 border-white/30 buffering-ring" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full border-2 border-white/50 border-t-transparent animate-spin" />
                  </div>
                </div>
              </div>
            )}

            {/* Error overlay */}
            {hasError && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/90">
                <div className="text-center px-8">
                  <p className="text-white/70 text-sm mb-4">
                    No se pudo cargar
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={retryVideo}
                      className="px-5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full text-sm transition-colors"
                    >
                      Reintentar
                    </button>
                    {currentIndex < playbackIds.length - 1 && (
                      <button
                        onClick={goToNext}
                        className="text-white/40 hover:text-white/60 text-xs"
                      >
                        Siguiente
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Pause overlay */}
            {!isPlaying && !isBuffering && !hasError && (
              <div className="absolute inset-0 z-15 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                  <Play className="w-8 h-8 text-white ml-1" fill="white" />
                </div>
              </div>
            )}

            <MuxPlayer
              key={`reel-${currentIndex}`}
              ref={playerRef}
              streamType="on-demand"
              playbackId={currentPlaybackId}
              autoPlay
              muted={isMuted}
              playsInline
              preload="auto"
              // Disable unnecessary features to reduce network requests
              disableCookies
              storyboardSrc=""
              thumbnailTime={0}
              startTime={0}
              onCanPlay={handleCanPlay}
              onPlay={handlePlay}
              onPause={handlePause}
              onWaiting={handleWaiting}
              onPlaying={handlePlaying}
              onEnded={handleEnded}
              onError={handleError}
              onTimeUpdate={handleTimeUpdate}
              className="reels-mux-player"
              style={{ width: "100%", height: "100%" }}
              {...(muxDataEnvKey ? { envKey: muxDataEnvKey } : {})}
              metadata={{
                video_id: currentPlaybackId,
                video_title: `reel-${currentIndex + 1}`,
                video_stream_type: "on-demand",
                player_name: "reels-feed",
                ...(muxDataMetadata || {}),
              }}
            />
          </div>
        )}

        {/* UI overlay */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10">
            <div
              className="h-full bg-white/80 progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Top controls */}
          <div className="absolute top-3 left-0 right-0 px-4 flex items-center justify-between">
            {/* Video counter */}
            <div className="px-2.5 py-1 bg-black/30 backdrop-blur-sm rounded-full text-white/80 text-xs font-medium">
              {currentIndex + 1}/{playbackIds.length}
            </div>

            {/* Mute button */}
            <button
              onClick={toggleMute}
              className="w-9 h-9 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white/80 pointer-events-auto active:scale-95 transition-transform"
              aria-label={isMuted ? "Activar sonido" : "Silenciar"}
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Navigation arrows (desktop only) */}
          {!isMobile && (
            <>
              {currentIndex > 0 && (
                <button
                  onClick={goToPrevious}
                  className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 -mt-20 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white/60 hover:text-white pointer-events-auto transition-all"
                  aria-label="Anterior"
                >
                  <ChevronUp className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={goToNext}
                className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 mt-20 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white/60 hover:text-white pointer-events-auto transition-all"
                aria-label="Siguiente"
              >
                <ChevronDown className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Swipe hint (first video on mobile) */}
          {isMobile && currentIndex === 0 && playbackIds.length > 1 && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 animate-bounce pointer-events-none">
              <ChevronUp className="w-6 h-6 text-white/40" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReelsFeed;
